import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import VideoPlayerModal from './VideoPlayerModal';

const VIDEO_ROWS = [
  { id: 'welcome', label: 'Welcome Video', description: 'Shown to all participants on the Dashboard.' },
  { id: 'day1', label: 'Day 1', description: 'Plays at the top of Workbook Day 1.' },
  { id: 'day2', label: 'Day 2', description: 'Plays at the top of Workbook Day 2.' },
  { id: 'day3', label: 'Day 3', description: 'Plays at the top of Workbook Day 3.' },
  { id: 'day4', label: 'Day 4', description: 'Plays at the top of Workbook Day 4.' },
  { id: 'day5', label: 'Day 5', description: 'Plays at the top of Workbook Day 5.' },
  { id: 'day6', label: 'Day 6', description: 'Plays at the top of Workbook Day 6.' },
  { id: 'day7', label: 'Day 7', description: 'Plays at the top of Workbook Day 7.' },
  { id: 'day8', label: 'Day 8', description: 'Plays at the top of Workbook Day 8.' },
  { id: 'day9', label: 'Day 9', description: 'Plays at the top of Workbook Day 9.' },
  { id: 'day10', label: 'Day 10', description: 'Plays at the top of Workbook Day 10.' },
];

export default function ManageVideos() {
  const [videos, setVideos] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [msg, setMsg] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState({ url: '', title: '' });

  useEffect(() => { loadVideos(); }, []);

  const loadVideos = async () => {
    const snap = await getDocs(collection(db, 'videos'));
    const map = {};
    snap.docs.forEach(d => {
      map[d.id] = d.data();
    });
    const initialized = {};
    VIDEO_ROWS.forEach(row => {
      initialized[row.id] = {
        embedUrl: map[row.id]?.embedUrl || '',
        active: map[row.id]?.active ?? false,
      };
    });
    setVideos(initialized);
    setLoading(false);
  };

  const handleUrlChange = (id, value) => {
    setVideos(prev => ({ ...prev, [id]: { ...prev[id], embedUrl: value } }));
  };

  const handleToggleActive = (id) => {
    setVideos(prev => ({ ...prev, [id]: { ...prev[id], active: !prev[id].active } }));
  };

  const handleSave = async (id) => {
    setSaving(prev => ({ ...prev, [id]: true }));
    try {
      await setDoc(doc(db, 'videos', id), {
        embedUrl: videos[id].embedUrl || '',
        active: videos[id].active || false,
      });
      setMsg({ [id]: { type: 'success', text: 'Saved.' } });
      setTimeout(() => setMsg({}), 2500);
    } catch (err) {
      setMsg({ [id]: { type: 'error', text: 'Could not save. Try again.' } });
    }
    setSaving(prev => ({ ...prev, [id]: false }));
  };

  const handlePreview = (id, label) => {
    const url = videos[id]?.embedUrl;
    if (!url) {
      setMsg({ [id]: { type: 'error', text: 'Paste a URL first, then preview.' } });
      setTimeout(() => setMsg({}), 2500);
      return;
    }
    setPreviewVideo({ url, title: label });
    setPreviewOpen(true);
  };

  if (loading) {
    return <div style={s.loading}>Loading videos...</div>;
  }

  return (
    <div style={s.container}>
      <div style={s.headerBlock}>
        <h1 style={s.heading}>Manage Videos</h1>
        <p style={s.sub}>
          Paste Adilo embed URLs for the welcome video and each day. Toggle Active to make a video visible to participants. Use Preview to confirm the video plays before saving.
        </p>
      </div>

      <div style={s.rowsWrapper}>
        {VIDEO_ROWS.map(row => {
          const video = videos[row.id] || { embedUrl: '', active: false };
          return (
            <div key={row.id} style={s.row}>
              <div style={s.rowTop}>
                <div style={s.rowLabel}>
                  <div style={s.rowTitle}>{row.label}</div>
                  <div style={s.rowDesc}>{row.description}</div>
                </div>
                <button
                  style={{ ...s.activeBtn, ...(video.active ? s.activeBtnOn : {}) }}
                  onClick={() => handleToggleActive(row.id)}
                >
                  {video.active ? '✓ Active' : 'Hidden'}
                </button>
              </div>

              <div style={s.urlRow}>
                <input
                  style={s.input}
                  type="text"
                  placeholder="https://adilo.bigcommand.com/watch/..."
                  value={video.embedUrl}
                  onChange={e => handleUrlChange(row.id, e.target.value)}
                />
                <button
                  style={s.previewBtn}
                  onClick={() => handlePreview(row.id, row.label)}
                >
                  Preview
                </button>
                <button
                  style={s.saveBtn}
                  onClick={() => handleSave(row.id)}
                  disabled={saving[row.id]}
                >
                  {saving[row.id] ? 'Saving...' : 'Save'}
                </button>
              </div>

              {msg[row.id] && (
                <div style={{ ...s.msgBox, ...(msg[row.id].type === 'error' ? s.msgError : s.msgSuccess) }}>
                  {msg[row.id].text}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <VideoPlayerModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        embedUrl={previewVideo.url}
        title={previewVideo.title}
      />
    </div>
  );
}

const s = {
  container: { padding: '24px 32px', maxWidth: '1100px' },
  loading: { padding: '40px', textAlign: 'center', color: '#3D3D3D', fontFamily: 'DM Sans, sans-serif' },
  headerBlock: { marginBottom: '24px' },
  heading: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A', marginBottom: '8px' },
  sub: { fontSize: '13px', color: '#3D3D3D', fontWeight: '300', lineHeight: '1.6', maxWidth: '700px' },
  rowsWrapper: { display: 'flex', flexDirection: 'column', gap: '16px' },
  row: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '18px 20px' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', gap: '16px' },
  rowLabel: { flex: 1 },
  rowTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', fontWeight: '700', color: '#1B2A4A', marginBottom: '2px' },
  rowDesc: { fontSize: '11px', color: '#3D3D3D', fontWeight: '300' },
  activeBtn: {
    background: '#F2F0EC',
    color: '#3D3D3D',
    border: '1.5px solid #E8E6E2',
    padding: '6px 14px',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  activeBtnOn: {
    background: '#eaf3de',
    color: '#3b6d11',
    borderColor: '#3b6d11',
  },
  urlRow: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  input: {
    flex: 1,
    minWidth: '300px',
    border: '1.5px solid #E8E6E2',
    padding: '10px 14px',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '13px',
    color: '#3D3D3D',
    background: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
  },
  previewBtn: {
    background: '#F2F0EC',
    color: '#1B2A4A',
    border: '1.5px solid #E8E6E2',
    padding: '10px 16px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
    whiteSpace: 'nowrap',
  },
  saveBtn: {
    background: '#1B2A4A',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
    whiteSpace: 'nowrap',
  },
  msgBox: { fontSize: '12px', padding: '8px 12px', marginTop: '10px' },
  msgError: { background: '#fdecea', color: '#a32d2d' },
  msgSuccess: { background: '#eaf3de', color: '#3b6d11' },
};
